import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function hoyChihuahua() {
  // UTC-6 fijo
  const now = new Date(Date.now() - 6 * 60 * 60 * 1000);
  return now.toISOString().slice(0, 10);
}

function fmtFecha(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const meses = ["enero","febrero","marzo","abril","mayo","junio",
                  "julio","agosto","septiembre","octubre","noviembre","diciembre"];
  return `${parseInt(d)} de ${meses[parseInt(m) - 1]} de ${y}`;
}

function fmtNumero(n) {
  return (n ?? 0).toLocaleString("es-MX");
}

function MetricCard({ icon, label, value, sub, color = "blue" }) {
  const colors = {
    blue:   "bg-blue-50   border-blue-200  text-blue-700",
    green:  "bg-green-50  border-green-200 text-green-700",
    teal:   "bg-teal-50   border-teal-200  text-teal-700",
    indigo: "bg-indigo-50 border-indigo-200 text-indigo-700",
  };
  return (
    <div className={`rounded-xl border p-4 flex flex-col gap-1 ${colors[color]}`}>
      <div className="text-2xl">{icon}</div>
      <div className="text-2xl font-bold leading-tight">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wide opacity-70">{label}</div>
      {sub && <div className="text-xs opacity-60 mt-0.5">{sub}</div>}
    </div>
  );
}

function TipoBadge({ tipo, origenCrc }) {
  const t = (tipo || "").toUpperCase();
  if (t === "CRC_OFICIO") {
    if (origenCrc === "CRC")
      return <span className="px-1.5 py-0.5 rounded text-xs bg-red-100 text-red-800 font-semibold">CRC</span>;
    if (origenCrc === "CIUDADANO")
      return <span className="px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-800 font-medium">Ciudadano</span>;
    return <span className="px-1.5 py-0.5 rounded text-xs bg-amber-100 text-amber-800 font-medium">Oficio</span>;
  }
  if (t === "RIEGO" || t === "CAMELLÓN") return <span className="px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-800 font-medium">{tipo}</span>;
  if (t.includes("TINACO") && t.includes("POTABLE")) return <span className="px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-800 font-medium">{tipo}</span>;
  if (t.includes("TINACO")) return <span className="px-1.5 py-0.5 rounded text-xs bg-teal-100 text-teal-800 font-medium">{tipo}</span>;
  if (t.includes("TALLER")) return <span className="px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-800 font-medium">{tipo}</span>;
  if (t.includes("LLENADO")) return <span className="px-1.5 py-0.5 rounded text-xs bg-cyan-100 text-cyan-800 font-medium">{tipo}</span>;
  return <span className="px-1.5 py-0.5 rounded text-xs bg-slate-100 text-slate-700 font-medium">{tipo}</span>;
}

export default function ActividadPublica() {
  const [params, setParams] = useSearchParams();
  const fechaParam = params.get("fecha") || hoyChihuahua();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [extraEventos, setExtraEventos] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [busqueda, setBusqueda] = useState("");

  const cargar = useCallback(async (fecha, resetExtras = true) => {
    setLoading(true);
    setErr(null);
    if (resetExtras) {
      setExtraEventos([]);
      setCurrentPage(1);
    }
    try {
      const r = await axios.get(`${API}/publico/actividad`, { params: { fecha, page: 1, page_size: 20 } });
      setData(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || "Error al cargar la actividad del día.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(fechaParam); }, [fechaParam, cargar]);

  const cargarMas = async () => {
    if (!data || loadingMore) return;
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    try {
      const r = await axios.get(`${API}/publico/actividad`, {
        params: { fecha: fechaParam, page: nextPage, page_size: 20 },
      });
      setExtraEventos(prev => [...prev, ...r.data.eventos]);
      setCurrentPage(nextPage);
    } catch (_) {}
    setLoadingMore(false);
  };

  const cambiarFecha = (e) => {
    setParams({ fecha: e.target.value });
  };

  const todosEventos = data ? [...data.eventos, ...extraEventos] : [];
  const hayMas = data && (currentPage < data.total_pages);

  const eventosFiltrados = busqueda.trim()
    ? (() => {
        const q = busqueda.trim().toLowerCase();
        return todosEventos.filter(ev =>
          (ev.lugar || "").toLowerCase().includes(q) ||
          String(ev.pipa || "").toLowerCase().includes(q) ||
          (ev.folio || "").toLowerCase().includes(q)
        );
      })()
    : todosEventos;

  if (loading) return (
    <div className="min-h-screen grid place-items-center bg-slate-50">
      <div className="text-slate-500 text-sm">Cargando actividad…</div>
    </div>
  );

  if (err) return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 max-w-md w-full text-center">
        <div className="text-4xl mb-3">⚠️</div>
        <p className="text-slate-700 text-sm">{err}</p>
        <button onClick={() => cargar(fechaParam)} className="mt-4 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm">
          Reintentar
        </button>
      </div>
    </div>
  );

  const m = data.metricas;
  const hayLitros = m.litros_potable > 0 || m.litros_tratada > 0;
  const tallerList = data.pipas_en_taller || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 px-4 py-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-xl font-bold text-slate-900">Actividad del día</h1>
                <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200">
                  Acceso público · sin usuario
                </span>
              </div>
              <p className="text-sm text-slate-500 mt-0.5">{fmtFecha(data.fecha)} · DMU Chihuahua</p>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Ver fecha:</label>
              <input
                type="date"
                value={fechaParam}
                onChange={cambiarFecha}
                className="text-sm border border-slate-300 rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">

        {/* Métricas */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <MetricCard
            icon="🚛"
            label="Pipas en ruta"
            value={m.pipas_en_ruta}
            color="blue"
          />
          <MetricCard
            icon="💧"
            label="Puntos de riego"
            value={m.puntos_riego}
            color="green"
          />
          <MetricCard
            icon="🚰"
            label="Tinacos llenados"
            value={m.tinacos_llenados}
            sub={m.tinacos_llenados > 0 ? `potable: ${m.tinacos_potable} · tratada: ${m.tinacos_tratada}` : null}
            color="teal"
          />
          <div className="rounded-xl border bg-indigo-50 border-indigo-200 text-indigo-700 p-4 flex flex-col gap-1">
            <div className="text-2xl">📈</div>
            <div className="text-2xl font-bold leading-tight">
              {hayLitros ? `~${fmtNumero(m.litros_total)} L` : "—"}
            </div>
            <div className="text-xs font-semibold uppercase tracking-wide opacity-70">Agua distribuida</div>
            {hayLitros ? (
              <div className="mt-1 flex flex-col gap-0.5">
                {m.litros_potable > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-blue-700">💧 Potable</span>
                    <span className="font-bold text-blue-800">~{fmtNumero(m.litros_potable)} L</span>
                  </div>
                )}
                {m.litros_tratada > 0 && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-medium text-teal-700">♻️ Tratada</span>
                    <span className="font-bold text-teal-800">~{fmtNumero(m.litros_tratada)} L</span>
                  </div>
                )}
              </div>
            ) : m.sin_capacidad_count > 0 ? (
              <div className="text-xs opacity-60 mt-0.5">sin capacidad registrada</div>
            ) : null}
          </div>
        </div>

        {/* Aviso sin capacidad */}
        {m.sin_capacidad_count > 0 && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
            ⚠️ {m.sin_capacidad_count} intervención(es) no incluidas en el total por no tener capacidad de pipa registrada.
          </div>
        )}

        {/* Pipas en taller */}
        <div>
          <h2 className="font-semibold text-slate-800 mb-3">
            🔧 Pipas en taller
            {tallerList.length > 0 && (
              <span className="ml-2 text-sm font-normal text-slate-400">({tallerList.length})</span>
            )}
          </h2>
          {tallerList.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-500">
              Ninguna pipa en taller ahora mismo.
            </div>
          ) : (
            <div className="rounded-xl border border-amber-200 bg-amber-50 divide-y divide-amber-100 overflow-hidden">
              {tallerList.map((p, i) => (
                <div key={i} className="flex items-start gap-3 px-4 py-3">
                  <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-amber-200 grid place-items-center text-lg">🔧</div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-amber-900">Pipa #{p.pipa_numero}</p>
                    <p className="text-xs text-amber-800 mt-0.5">{p.motivo}</p>
                    {p.lugar_taller && (
                      <p className="text-xs text-amber-700 mt-0.5">📍 {p.lugar_taller}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <p className="text-xs text-amber-700">{p.fecha_entrada}</p>
                    {p.dias_en_taller !== null && p.dias_en_taller !== undefined && (
                      <p className="text-xs font-bold text-amber-900 mt-0.5">
                        {p.dias_en_taller === 0 ? "Hoy" : `${p.dias_en_taller} día${p.dias_en_taller !== 1 ? "s" : ""}`}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Lista de intervenciones */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="font-semibold text-slate-800">
              Registro de intervenciones
              <span className="ml-2 text-sm font-normal text-slate-400">
                ({busqueda.trim() ? `${eventosFiltrados.length} de ${todosEventos.length}` : data.total_eventos})
              </span>
            </h2>
            <input
              type="search"
              placeholder="Buscar lugar, pipa o folio…"
              value={busqueda}
              onChange={e => setBusqueda(e.target.value)}
              className="text-sm border border-slate-300 rounded-lg px-3 py-1.5 bg-white w-full sm:w-56 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {eventosFiltrados.length === 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-8 text-center text-slate-400 text-sm">
              {busqueda.trim()
                ? `Sin resultados para "${busqueda}".`
                : "Sin intervenciones registradas para esta fecha."}
            </div>
          ) : (
            <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
              {eventosFiltrados.map((ev) => (
                <div key={ev.id} className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors">
                  {/* Miniatura */}
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                    {ev.foto_url
                      ? <img src={ev.foto_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                      : <div className="w-full h-full grid place-items-center text-slate-300 text-lg">📷</div>
                    }
                  </div>

                  {/* Contenido */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                      <TipoBadge tipo={ev.tipo} origenCrc={ev.origen_crc} />
                      {ev.pipa && (
                        <span className="text-xs text-slate-400">pipa #{ev.pipa}</span>
                      )}
                      {ev.folio && (
                        <span className="text-xs text-slate-400 font-mono">{ev.folio}</span>
                      )}
                    </div>
                    <p className="text-sm font-medium text-slate-800 truncate">{ev.lugar}</p>
                    <p className="text-xs text-slate-400">{ev.chofer}{ev.companero ? ` · ${ev.companero}` : ""}</p>
                  </div>

                  {/* Ver evidencia */}
                  {ev.evidencia_url && (
                    <a
                      href={ev.evidencia_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex-shrink-0 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                    >
                      Ver
                    </a>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Cargar más */}
          {hayMas && (
            <div className="mt-4 text-center">
              <button
                onClick={cargarMas}
                disabled={loadingMore}
                className="px-5 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              >
                {loadingMore ? "Cargando…" : `Cargar más (${data.total_eventos - todosEventos.length} restantes)`}
              </button>
            </div>
          )}
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-400 pb-4">
          DMU Chihuahua · Dirección Municipal de Urbanismo · {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
